import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpETLComponentTypeManifest } from '../interfaces';
import { TpETLObjectTypes } from '../../etl-data-types/interfaces';
import { TpETLPipelineComponent, TpETLDryRun, TpETLDryRunComponentResult } from '../../interfaces';
import {
  createConfigParamTypeExistingColumn,
  createConfigParamTypeExistingColumnList,
  createConfigParamTypeNewColumn,
  ETLDryRunObjectTableAddColumn,
  ETLDryRunObjectTableAddColumnFromParam,
} from '../utils';
import { addDryRunObjectTable, getETLComponentInputObjectTable, getETLTableObjectColumn } from '../../utils';
import { addETLDryRunError } from '../../etlDryRun';
import { TpJsonColumnInfo, applyJsonColumnsToDryRun, JsonColumnEditorList } from '../json-to-table/etlJsonUtils';
import { CONFIGPARAMTYPES } from '../../../visual-analytics/helpers/configParams';

function performDryRun(dryRun: TpETLDryRun, comp: TpETLPipelineComponent): TpETLDryRunComponentResult {
  const inputTableObj = getETLComponentInputObjectTable(dryRun, comp, 'sourceTable');
  const outputTableObj = addDryRunObjectTable(dryRun, comp.outputObjectIds['result'], comp);

  // Add new index column for the new rows.
  ETLDryRunObjectTableAddColumnFromParam(outputTableObj, comp, 'newIndexColumn');

  try {
    const colIdList = (comp.params['transferColumns'] || []) as string[];
    for (const colId of colIdList) {
      const sourceColInfo = getETLTableObjectColumn(inputTableObj, colId);
      ETLDryRunObjectTableAddColumn(
        outputTableObj,
        sourceColInfo.identifier,
        sourceColInfo.name,
        sourceColInfo.description,
        sourceColInfo.dataType,
      );
    }
  } catch (error) {
    addETLDryRunError(dryRun, String(error), comp.id, 'transferColumns');
  }

  const level1Columns = (comp.params['level1JsonColumns'] || []) as TpJsonColumnInfo[];
  const level2Columns = (comp.params['level2JsonColumns'] || []) as TpJsonColumnInfo[];

  applyJsonColumnsToDryRun(outputTableObj, level1Columns, dryRun, comp.id);
  applyJsonColumnsToDryRun(outputTableObj, level2Columns, dryRun, comp.id);

  return { abort: false };
}

function renderCustomConfigParams(
  dryRun: TpETLDryRun,
  comp: TpETLPipelineComponent,
  updateEditingComponent: (newComp: TpETLPipelineComponent) => void,
): any {
  const level1Columns = (comp.params['level1JsonColumns'] || []) as TpJsonColumnInfo[];
  const level2Columns = (comp.params['level2JsonColumns'] || []) as TpJsonColumnInfo[];

  return (
    <div>
      <JsonColumnEditorList
        title="Level 1 - Parent Object Fields"
        description="Define keys from the root JSON dictionary that will be repeated across every exploded array row."
        columns={level1Columns}
        dryRun={dryRun}
        compId={comp.id}
        onChange={(newCols) => {
          comp.params['level1JsonColumns'] = newCols;
          updateEditingComponent(comp);
        }}
      />

      <JsonColumnEditorList
        title="Level 2 - Nested Array Fields"
        description="Define structural property keys evaluated inside the nested dictionary list items."
        columns={level2Columns}
        dryRun={dryRun}
        compId={comp.id}
        onChange={(newCols) => {
          comp.params['level2JsonColumns'] = newCols;
          updateEditingComponent(comp);
        }}
      />
    </div>
  );
}

export const manifestETLJsonNestedExplode: TpETLComponentTypeManifest = {
  id: 'JsonNestedExplode',
  name: 'Convert Nested JSON to Table with Explode',
  description:
    'Converts a JSON column with a nested array into a new table with additional rows by exploding the nested array.',
  categoryId: 'json',
  icon: <FontAwesomeIcon icon={['fas', 'table']} />,
  inputs: [{ type: TpETLObjectTypes.TABLE, id: 'sourceTable', name: 'Source Table', columns: [] }],
  outputs: [
    {
      id: 'result',
      name: 'Result table',
      type: TpETLObjectTypes.TABLE,
      description: 'Table with new primary key, extra rows and columns from the JSON conversion',
      columns: [],
    },
  ],
  params: [
    {
      id: 'indexColumn',
      name: 'Current Index Column',
      description:
        'Existing column with unique values for each row (e.g., "pmid"). Used to generate new index for exploded rows (e.g., "pmid_1", "pmid_2", etc.).',
      paramType: createConfigParamTypeExistingColumn('sourceTable'),
    },
    {
      id: 'newIndexColumn',
      name: 'New Index Column',
      description: 'New column to store the generated index for exploded rows.',
      paramType: createConfigParamTypeNewColumn('sourceTable'),
    },
    {
      id: 'jsonSourceColumn',
      name: 'JSON Source Column',
      description: 'Incoming multi-level JSON string column.',
      paramType: createConfigParamTypeExistingColumn('sourceTable'),
    },
    {
      id: 'nestedListKey',
      name: 'Nested Array Key Name',
      description: 'JSON dictionary key containing the array block (e.g., "treatment_arms").',
      paramType: {
        configParamTypeId: CONFIGPARAMTYPES.String,
        defaultValue: '',
      },
    },
    {
      id: 'armIdSlugKey',
      name: 'Array Element Slug Key Name',
      description: 'Key inside list elements for unique slugs (e.g., "arm_id_slug").',
      paramType: {
        configParamTypeId: CONFIGPARAMTYPES.String,
        defaultValue: '',
      },
    },
    {
      id: 'transferColumns',
      name: 'Transfer Columns',
      description: 'Table columns duplicated downstream.',
      paramType: createConfigParamTypeExistingColumnList('sourceTable'),
    },
  ],
  performDryRun,
  renderCustomConfigParams,
};
